import type { FeatureContext, MenuItem } from './featureContext';

/**
 * The Bank Escort's client affordances (PRD #371): the ask-destination
 * double-click and the escort transaction long-press menu. The menu is
 * **exclusive** - keyed off the replicated `escort` component (a generated name
 * defeats any name check - the justice lesson) and claiming the whole menu so an
 * escort can never grow a duel/dice row. "Ask destination" plus "Accept escort"
 * (or "Abandon escort" for the player currently leading it, read off the
 * replicated `escort.escorter`). The server rules authoritatively on
 * range/phase/the first-accept race; an invalid pick fizzles (or earns the
 * ServUO "I am already being led!").
 */
export function installEscort(ctx: FeatureContext): void {
  ctx.onRendererBuilt((renderer) => {
    // Double-clicking the Bank Escort asks its destination - intent only; the
    // escort answers with its overhead ask line. The server enforces the Chebyshev
    // range against the asker's authoritative tile, so an out-of-range
    // double-click fizzles silently.
    renderer.onEscortAsk = (npc) => {
      const playerId = ctx.playerId();
      if (playerId !== undefined) {
        ctx.client()?.sendCommand({ kind: 'EscortAsk', entity: playerId, npc });
      }
    };
  });

  ctx.menu.addEntityMenu((target) => {
    const escort = target.entity.escort;
    if (escort === undefined) return null;
    const playerId = ctx.playerId();
    const items: MenuItem[] = [
      {
        label: 'Ask destination',
        onSelect: () => {
          const me = ctx.playerId();
          if (me !== undefined) {
            ctx.client()?.sendCommand({ kind: 'EscortAsk', entity: me, npc: target.id });
          }
        },
      },
    ];
    if (escort.escorter !== undefined && escort.escorter === playerId) {
      items.push({
        label: 'Abandon escort',
        onSelect: () => {
          const me = ctx.playerId();
          if (me !== undefined) {
            ctx.client()?.sendCommand({ kind: 'EscortAbandon', entity: me, npc: target.id });
          }
        },
      });
    } else {
      items.push({
        label: 'Accept escort',
        onSelect: () => {
          const me = ctx.playerId();
          if (me !== undefined) {
            ctx.client()?.sendCommand({ kind: 'EscortAccept', entity: me, npc: target.id });
          }
        },
      });
    }
    return { items, exclusive: true };
  });
}
